import { EventEmitter }             from '@angular/core';
import { ErsPresetEnum }            from '@app/modules/ers/models/ers.model';
import { FccInstallationTypeEnum }  from '@app/modules/fcc/models/fcc.model';
import {
	ITssEntityTooltipConfig,
	TssEntityTooltipIdentifierEnum
}                                   from '@app/modules/tss/components/tooltip/tss-entity-tooltip/tss-entity-tooltip.model';
import { TssFormProp }              from '@app/modules/tss/forms/tss-form/tss-form.model';
import {
	TssAssignmentTypeEnum,
	TssExternalProp,
	TssExternalProps,
	TssExternalStatusEnum,
	TssModeWithSubTypeEnum,
	TssPresetEnum,
	TssProp,
	TssProps,
	TssStatus,
	TssStatusEnum
}                                   from '@app/modules/tss/models/tss.model';
import { APP_CONFIG }               from '@appConfig/_app-config_master';
import { DeviceTypeEnum }           from '@appConfig/media-config';
import { brandConfig }              from '@brand/configs/brand.config';
import {
	AllowedDetailsTabEnum,
	ITabContent
}                                   from '@core/interfaces/common.interfaces';
import {
	IFormData,
	IFormUpdateData
}                                   from '@core/interfaces/form.interface';
import { environment }              from '@env/environment';
import { EntityVariantEnum }        from '@models/entity.model';
import {
	IEntityVariantPresetRelation,
	IPresetRelation,
	PresetIdentifierEnum
}                                   from '@models/preset';
import {
	BusinessPremisesScopesEnum,
	OrganizationScopesEnum,
	TssScopesEnum
}                                   from '@models/scope.enums';
import { ActionEnum }               from '@shared/components/action-item/action-item.enum';
import {
	ButtonGroupItemType,
	IButtonGroupConfig
}                                   from '@shared/components/button-group/button-group.model';
import {
	IStatusFlow,
	OutOfFlowDisplayModeEnum
}                                   from '@shared/components/status-flow/status-flow.model';
import {
	FormValidatorConfigType,
	FormValidatorIdentifierEnum,
	FormValidatorsConfig,
	FormValidatorVariantEnum
}                                   from '@shared/forms/models/form.model';
import {
	ISortData,
	SortOrderEnum
}                                   from '@shared/list-operations/components/sort-filter/sort-filter.model';
import { LIST_OPERATIONS_METADATA } from '@shared/list-operations/config/list-operations.config';
import {
	FilterIdentifiersEnum,
	FilterTypes,
	IFilter,
	IFilterItem
}                                   from '@shared/list-operations/model/filter.model';
import {
	IListOperationsConfig,
	ListOperationsIdentifierEnum
}                                   from '@shared/list-operations/model/list-operations.model';
import { PagingModeEnum }           from '@shared/list-operations/model/paging.model';
import { ManagedObjectTypeEnum }    from '@shared/models/managed-object.model';
import { actionStatus }             from '@shared/services/toaster.service';
import { ObjectUtils }              from '@utils/helpers';


export interface IVirtualTssMetadata {
	buttonGroupConfigs: {
		list: IButtonGroupConfig;
	};
	entityId: string;
	entityTooltip: ITssEntityTooltipConfig;
	entityExternalTooltip: ITssEntityTooltipConfig;
	deactivationDatePastDaysBeforeDelete: number;
	details: {
		allowedDetailsTabs: AllowedDetailsTabEnum[],
		allowedDetailsTabsExternal: AllowedDetailsTabEnum[],
		dateFormat: Intl.DateTimeFormatOptions;
		internalTabContent: ITabContent[];
		internalTabContentTssExternal: ITabContent[];
	};
	form: {
		baseData: {
			[ key: string ]: IFormData
		};
		preserveData: IFormUpdateData[];
		validatorsTss: FormValidatorsConfig<TssFormProp>;
		validatorsTssExternal: FormValidatorsConfig<TssFormProp>;
	};
	i18n: {
		basePath: string;
		page: string;
		entity: string;
		form: string;
		details: string;
		tssLifecycleModal: string;
	};
	listOperations: IListOperationsConfig;
	listOperationsExternal: IListOperationsConfig;
	overview: {
		i18nPath: string;
	};
	presetsRelationByEntityVariant: IEntityVariantPresetRelation<PresetIdentifierEnum.TSS>;
	presetsRelationByPresetId: IPresetRelation<TssPresetEnum, PresetIdentifierEnum.TSS>;
	status: IStatusFlow<TssStatusEnum, TssScopesEnum>;
	statusTssExternal: IStatusFlow<TssExternalStatusEnum, TssScopesEnum>;
	statusAllowedToListTssUnderBusinessPremises: ( entityVariant: EntityVariantEnum ) => TssStatus[];
	supportedEntityVariants: EntityVariantEnum[];
}

const allowedDetailsTabs: AllowedDetailsTabEnum[] = [
	AllowedDetailsTabEnum.INSTALLATION_DETAILS,
	AllowedDetailsTabEnum.USER_AUDIT,
	AllowedDetailsTabEnum.INTERNAL
];
const allowedDetailsTabsExternal: AllowedDetailsTabEnum[] = [
	AllowedDetailsTabEnum.USER_AUDIT,
	AllowedDetailsTabEnum.INTERNAL
];
const availableTssModesWithSubType: TssModeWithSubTypeEnum[] = [
	...brandConfig.availableTssModesWithSubtype
].filter( ( tssModeWithSubType: TssModeWithSubTypeEnum ) => ![
	TssModeWithSubTypeEnum.TOKEN_CV1,
	TssModeWithSubTypeEnum.TOKEN_CV2
].includes( tssModeWithSubType ) );
const availableFccInstallationTypes: FccInstallationTypeEnum[] = [
	FccInstallationTypeEnum.FCC_DATACENTER,
	FccInstallationTypeEnum.LOCAL
];
const detailsFullInternalTabContent: ITabContent[] = [
	{
		key: 'tenantId',
		order: 1,
		i18nPath: 'tss.page.details.tenantId',
		addSpaceToPrevious: false,
		showOnDevices: [ DeviceTypeEnum.MOBILE, DeviceTypeEnum.TABLET, DeviceTypeEnum.DESKTOP ],
		copyClipboard: true
	},
	{
		key: 'organizationId',
		order: 2,
		i18nPath: 'tss.page.details.organizationId',
		addSpaceToPrevious: false,
		showOnDevices: [ DeviceTypeEnum.MOBILE, DeviceTypeEnum.TABLET, DeviceTypeEnum.DESKTOP ],
		copyClipboard: true
	},
	{
		key: 'businessPremisesId',
		order: 3,
		i18nPath: 'tss.page.details.businessPremisesId',
		addSpaceToPrevious: false,
		showOnDevices: [ DeviceTypeEnum.MOBILE, DeviceTypeEnum.TABLET, DeviceTypeEnum.DESKTOP ],
		copyClipboard: true
	},
	{
		key: 'fccId',
		order: 4,
		i18nPath: 'tss.page.details.fccId',
		addSpaceToPrevious: false,
		showOnDevices: [ DeviceTypeEnum.MOBILE, DeviceTypeEnum.TABLET, DeviceTypeEnum.DESKTOP ],
		copyClipboard: true
	},
	{
		key: 'id',
		order: 5,
		i18nPath: 'tss.page.details.id',
		addSpaceToPrevious: false,
		showOnDevices: [ DeviceTypeEnum.MOBILE, DeviceTypeEnum.TABLET, DeviceTypeEnum.DESKTOP ],
		copyClipboard: true
	}
];
const listButtonGroupItems: ButtonGroupItemType[] = [
	{ enforceLabelCustomTemplate: true, id: EntityVariantEnum.INTERNAL, order: 1 },
	{ enforceLabelCustomTemplate: true, id: EntityVariantEnum.EXTERNAL, order: 2 }
];
const supportedEntityVariants: EntityVariantEnum[] = [
	EntityVariantEnum.INTERNAL
];

const validatorsTss: FormValidatorsConfig<TssFormProp> = {
	bsiId: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [] ),
	deviceType: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [] ),
	ersSecret: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.PATTERN,
			[
				{
					identifier: FormValidatorIdentifierEnum.PATTERN,
					isEnabled: true,
					pattern: new RegExp( /^[0-9a-zA-Z]{4,}$/ ),
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.REQUIRED,
			[
				{
					identifier: FormValidatorIdentifierEnum.REQUIRED,
					isEnabled: true,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		]
	] ),
	name: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.MAX_LENGTH,
			[
				{
					identifier: FormValidatorIdentifierEnum.MAX_LENGTH,
					isEnabled: true,
					length: 300,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.PATTERN,
			[
				{
					identifier: FormValidatorIdentifierEnum.PATTERN,
					isEnabled: true,
					pattern: new RegExp( /^[^\t\r\n]+$/ ),
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.REQUIRED,
			[
				{
					identifier: FormValidatorIdentifierEnum.REQUIRED,
					isEnabled: true,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		]
	] ),
	status: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [] ),
	tssSerialNumber: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [] )
};

const validatorsTssExternal: FormValidatorsConfig<TssFormProp> = {
	bsiId: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.PATTERN,
			[
				{
					identifier: FormValidatorIdentifierEnum.PATTERN,
					isEnabled: true,
					pattern: new RegExp( /^\d{4}-\d{4}$/ ),
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.REQUIRED,
			[
				{
					identifier: FormValidatorIdentifierEnum.REQUIRED,
					isEnabled: true,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		]
	] ),
	deviceType: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [] ),
	ersSecret: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [] ),
	name: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.MAX_LENGTH,
			[
				{
					identifier: FormValidatorIdentifierEnum.MAX_LENGTH,
					isEnabled: true,
					length: 300,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.PATTERN,
			[
				{
					identifier: FormValidatorIdentifierEnum.PATTERN,
					isEnabled: true,
					pattern: new RegExp( /^[^\t\r\n]+$/ ),
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.REQUIRED,
			[
				{
					identifier: FormValidatorIdentifierEnum.REQUIRED,
					isEnabled: true,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		]
	] ),
	status: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.REQUIRED,
			[
				{
					identifier: FormValidatorIdentifierEnum.REQUIRED,
					isEnabled: true,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		]
	] ),
	tssSerialNumber: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.MAX_LENGTH,
			[
				{
					identifier: FormValidatorIdentifierEnum.MAX_LENGTH,
					isEnabled: true,
					length: 64,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.MIN_LENGTH,
			[
				{
					identifier: FormValidatorIdentifierEnum.MIN_LENGTH,
					isEnabled: true,
					length: 64,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.PATTERN,
			[
				{
					identifier: FormValidatorIdentifierEnum.PATTERN,
					isEnabled: true,
					pattern: new RegExp( /^[a-fA-F0-9]+$/ ),
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.REQUIRED,
			[
				{
					identifier: FormValidatorIdentifierEnum.REQUIRED,
					isEnabled: true,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		]
	] )
};

if ( environment.features.elster.enabled ) {
	supportedEntityVariants.push( EntityVariantEnum.EXTERNAL );
}

// ListOps
const MASTER_LIST_OPERATIONS_CONFIG: IListOperationsConfig = {
	id: ListOperationsIdentifierEnum.TSS_LIST_PAGE,
	pagingData: {
		mode: PagingModeEnum.Light,
		continuesPagesMode: false,
		defaultValues: {
			pageSize: LIST_OPERATIONS_METADATA.default.paging.pageSize ?? 20,
			pageNumber: LIST_OPERATIONS_METADATA.default.paging.pageNumber ?? 0
		}
	},
	searchData: {
		placeholderI18nPath: 'tss.search.placeholder'
	},
	sortData: [
		{ name: 'assignmentType', i18nPath: 'tss.sort.assignmentType.label' },
		{ name: 'name', i18nPath: 'shared.forms.name.label' },
		{ name: 'status', i18nPath: 'shared.forms.status.label' },
		{ name: 'fccId', i18nPath: 'tss.sort.fccId.label' },
		{ name: 'fccVersion', i18nPath: 'tss.sort.fccVersion.label' },
		{ name: 'mode', i18nPath: 'tss.sort.mode.label' },
		{ name: 'createdDate', i18nPath: 'tss.sort.createdDate.label' },
		{ name: 'lastModifiedDate', i18nPath: 'tss.sort.lastModifiedDate.label', defaultActive: true, sortOrder: SortOrderEnum.DESC }
	],
	filterData: [
		{
			order: 1,
			isRendered: true,
			isDisabled: false,
			filterId: FilterIdentifiersEnum.STATUS,
			type: FilterTypes.FILTER_LIST_TYPE,
			i18nPath: 'shared.forms.status',
			values: [
				TssStatusEnum.ACTIVE,
				TssStatusEnum.INACTIVE,
				TssStatusEnum.SUSPENDED
			],
			items: [
				{ value: TssStatusEnum.ACTIVE, i18nPath: 'shared.status.active' },
				{ value: TssStatusEnum.DISABLED, i18nPath: 'shared.status.disabled' },
				{ value: TssStatusEnum.INACTIVE, i18nPath: 'shared.status.inactive' },
				{ value: TssStatusEnum.SUSPENDED, i18nPath: 'shared.status.suspended' }
			]
		},
		{
			order: 2,
			isRendered: availableTssModesWithSubType.length > 1,
			isDisabled: false,
			filterId: FilterIdentifiersEnum.MODE,
			type: FilterTypes.FILTER_LIST_TYPE,
			i18nPath: 'tss.sort.mode',
			values: [ ...availableTssModesWithSubType ],
			items: [
				...availableTssModesWithSubType
				.map( ( tssModeWithSubType: TssModeWithSubTypeEnum ) => (
					{ value: tssModeWithSubType, i18nPath: `tss.modeWithSubTypes.${ tssModeWithSubType }` }
				) )
			]
		},
		{
			order: 3,
			isRendered: availableFccInstallationTypes.length > 1,
			isDisabled: false,
			filterId: FilterIdentifiersEnum.INSTALLATION_TYPE,
			type: FilterTypes.FILTER_LIST_TYPE,
			i18nPath: 'fcc.fccInstallation',
			values: [ ...availableFccInstallationTypes ],
			items: [
				...availableFccInstallationTypes
				.map( ( fccInstallationType: FccInstallationTypeEnum ) => (
					{ value: fccInstallationType, i18nPath: `fcc.fccInstallation.types.${ fccInstallationType }` }
				) )
			]
		},
		{
			order: 4,
			isRendered: true,
			isDisabled: false,
			filterId: FilterIdentifiersEnum.ASSIGNMENT,
			type: FilterTypes.FILTER_OPTION_TYPE,
			i18nPath: 'shared.forms.assignment',
			values: [],
			items: [
				{ value: TssAssignmentTypeEnum.BUSINESS_PREMISES, i18nPath: 'businessPremises.page.title' },
				{ value: TssAssignmentTypeEnum.ERS, i18nPath: 'ers.page.title' }
			]
		},
		{
			order: 5,
			isRendered: true,
			isDisabled: false,
			filterId: FilterIdentifiersEnum.FCC_VERSION,
			type: FilterTypes.FILTER_TEXT_TYPE,
			i18nPath: 'tss.filter.fccVersion',
			values: [],
			items: [
				{ i18nPath: 'tss.filter.fccVersion' }
			]
		},
		{
			order: 6,
			isRendered: true,
			isDisabled: false,
			filterId: FilterIdentifiersEnum.ORGANIZATION_IDS,
			type: FilterTypes.FILTER_SELECT_ORGANIZATION_TYPE,
			selectFilter: {
				paramKey: 'id',
				searchRestoreValueKey: 'name'
			},
			i18nPath: 'fiskalTssLogs.filter.tssByOrganization',
			service: 'organization',
			bindLabel: 'name',
			bindValue: 'name',
			resetEventEmitter: new EventEmitter<boolean>(),
			values: [],
			items: [],
			tooltip: {
				bindKey: 'name'
			},
			permissionConfig: {
				requiredScope: OrganizationScopesEnum.List,
				revokeScopeOnManagedObjectTypes: [
					ManagedObjectTypeEnum.BUSINESS_PREMISES,
					ManagedObjectTypeEnum.ORGANIZATION
				]
			}
		},
		{
			order: 7,
			isRendered: true,
			isDisabled: false,
			filterId: FilterIdentifiersEnum.BUSINESS_PREMISES_IDS,
			type: FilterTypes.FILTER_SELECT_BUSINESS_PREMISES_TYPE,
			selectFilter: {
				paramKey: 'id',
				searchRestoreValueKey: 'name'
			},
			i18nPath: 'fiskalTssLogs.filter.tssByBusinessPremises',
			service: 'businessPremises',
			bindLabel: 'name',
			bindValue: 'name',
			resetEventEmitter: new EventEmitter<boolean>(),
			values: [],
			items: [],
			tooltip: {
				bindKey: 'name'
			},
			permissionConfig: {
				requiredScope: BusinessPremisesScopesEnum.LIST,
				revokeScopeOnManagedObjectTypes: [
					ManagedObjectTypeEnum.BUSINESS_PREMISES
				]
			}
		},
		{
			order: 8,
			isRendered: true,
			isDisabled: false,
			filterId: FilterIdentifiersEnum.TSS_IDS,
			type: FilterTypes.FILTER_SELECT_TSS_TYPE,
			selectFilter: {
				paramKey: 'id',
				searchRestoreValueKey: 'name'
			},
			i18nPath: 'tss.filter.tssByFccId',
			service: 'tss',
			bindLabel: 'name',
			bindValue: 'name',
			resetEventEmitter: new EventEmitter<boolean>(),
			values: [],
			items: [],
			tooltip: {
				bindKey: 'name'
			}
		},
		{
			order: 9,
			isRendered: true,
			isDisabled: false,
			filterId: FilterIdentifiersEnum.TSS_IDS,
			type: FilterTypes.FILTER_TEXT_TYPE,
			i18nPath: 'tss.filter.tssByTssId',
			values: [],
			items: [
				{ i18nPath: 'tss.filter.tssByTssId' }
			]
		}
	]
};
const listOperationsConfigTss = ( listOperationsIdentifier: ListOperationsIdentifierEnum ): IListOperationsConfig => {
	// Sort
	const sortWhiteList: TssProps = [
		'assignmentType',
		'createdDate',
		'fccId',
		'fccVersion',
		'lastModifiedDate',
		'mode',
		'name',
		'status'
	];

	const sortData: ISortData[] = MASTER_LIST_OPERATIONS_CONFIG.sortData
	.filter( ( sortDataItem: ISortData ) => sortWhiteList.includes( sortDataItem.name as TssProp ) );

	// Filter
	const filterWhiteList: FilterIdentifiersEnum[] = [
		FilterIdentifiersEnum.ASSIGNMENT,
		FilterIdentifiersEnum.BUSINESS_PREMISES_IDS,
		FilterIdentifiersEnum.FCC_VERSION,
		FilterIdentifiersEnum.INSTALLATION_TYPE,
		FilterIdentifiersEnum.MODE,
		FilterIdentifiersEnum.ORGANIZATION_IDS,
		FilterIdentifiersEnum.STATUS,
		FilterIdentifiersEnum.TSS_IDS
	];

	const filterData: IFilter[] = MASTER_LIST_OPERATIONS_CONFIG.filterData
	.filter( ( filter: IFilter ) => filterWhiteList.includes( filter.filterId ) )
	.map( ( filter: IFilter ) => {
		if ( filter.type === FilterTypes.FILTER_SELECT_TSS_TYPE ) {
			return { ...filter, selectFilter: { ...filter.selectFilter, presetId: TssPresetEnum.TSS } };
		}

		return filter;
	} );

	return {
		...MASTER_LIST_OPERATIONS_CONFIG,
		id: listOperationsIdentifier,
		sortData,
		filterData
	};
};
const listOperationsConfigTssExternal = ( listOperationsIdentifier: ListOperationsIdentifierEnum ): IListOperationsConfig => {
	// Sort
	const sortWhiteList: TssExternalProps = [
		'createdDate',
		'lastModifiedDate',
		'name',
		'status'
	];

	const sortData: ISortData[] = MASTER_LIST_OPERATIONS_CONFIG.sortData
	.filter( ( sortDataItem: ISortData ) => sortWhiteList.includes( sortDataItem.name as TssExternalProp ) );

	// Filter
	const filterWhiteList: FilterIdentifiersEnum[] = [
		FilterIdentifiersEnum.BUSINESS_PREMISES_IDS,
		FilterIdentifiersEnum.FILTER,
		FilterIdentifiersEnum.ORGANIZATION_IDS,
		FilterIdentifiersEnum.STATUS
		/*
		 * TODO Elster/EaaS (commented out, because config has two filters with same
		 *  identifier tssIds & FCC_ID, requires listOps refactoring; see DFDEV-1660)
		 */
		// FilterIdentifiersEnum.TSS_IDS
	];

	const filterData: IFilter[] = MASTER_LIST_OPERATIONS_CONFIG.filterData
	.filter( ( filter: IFilter ) => filterWhiteList.includes( filter.filterId ) )
	.map( ( filter: IFilter ) => {

		// TSS SelectFilters
		if ( filter.type === FilterTypes.FILTER_SELECT_TSS_TYPE ) {
			return { ...filter, selectFilter: { ...filter.selectFilter, presetId: TssPresetEnum.TSS_EXTERNAL } };
		}

		// StatusFilter
		if ( filter.filterId === FilterIdentifiersEnum.STATUS ) {
			const values: TssExternalStatusEnum[] = [
				TssExternalStatusEnum.ACTIVE,
				TssExternalStatusEnum.INACTIVE
			];

			const items: IFilterItem[] = [
				{ value: TssExternalStatusEnum.ACTIVE, i18nPath: 'shared.status.active' },
				{ value: TssExternalStatusEnum.DISABLED, i18nPath: 'shared.status.disabled' },
				{ value: TssExternalStatusEnum.INACTIVE, i18nPath: 'shared.status.inactive' }
			];

			return { ...filter, items, values };
		}

		return filter;
	} );

	return {
		...MASTER_LIST_OPERATIONS_CONFIG,
		id: listOperationsIdentifier,
		sortData,
		filterData
	};
};


export const VIRTUAL_TSS_METADATA: IVirtualTssMetadata = ObjectUtils.deepCopyByRecursion( {
	buttonGroupConfigs: {
		list: {
			items: listButtonGroupItems?.filter(
				( buttonGroupItemType: ButtonGroupItemType ) => supportedEntityVariants.includes( buttonGroupItemType.id as EntityVariantEnum )
			)
		}
	},
	entityId: APP_CONFIG.tss.entityId,
	entityTooltip: {
		allowedTssIdentifiers: [
			TssEntityTooltipIdentifierEnum.VERSION,
			TssEntityTooltipIdentifierEnum.PROVIDED_BY,
			TssEntityTooltipIdentifierEnum.NAME,
			TssEntityTooltipIdentifierEnum.STATUS,
			TssEntityTooltipIdentifierEnum.ASSIGNMENT_TYPE,
			TssEntityTooltipIdentifierEnum.DEVICE_TYPE,
			TssEntityTooltipIdentifierEnum.MODE,
			TssEntityTooltipIdentifierEnum.MODE_SUB_TYPE,
			TssEntityTooltipIdentifierEnum.BSI_ID,
			TssEntityTooltipIdentifierEnum.FCC_INSTALLATION_TYPE,
			TssEntityTooltipIdentifierEnum.FCC_ID,
			TssEntityTooltipIdentifierEnum.FCC_VERSION,
			TssEntityTooltipIdentifierEnum.CREATED_DATE,
			TssEntityTooltipIdentifierEnum.LAST_MODIFIED_DATE,
			TssEntityTooltipIdentifierEnum.ACTIVATION_DATE,
			TssEntityTooltipIdentifierEnum.DEACTIVATION_DATE,
		],
		dateTimeFormat: { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }
	},
	entityExternalTooltip: {
		allowedTssIdentifiers: [
			TssEntityTooltipIdentifierEnum.VERSION,
			TssEntityTooltipIdentifierEnum.PROVIDED_BY,
			TssEntityTooltipIdentifierEnum.NAME,
			TssEntityTooltipIdentifierEnum.STATUS,
			TssEntityTooltipIdentifierEnum.DEVICE_TYPE,
			TssEntityTooltipIdentifierEnum.BSI_ID,
			TssEntityTooltipIdentifierEnum.CREATED_DATE,
			TssEntityTooltipIdentifierEnum.LAST_MODIFIED_DATE,
			TssEntityTooltipIdentifierEnum.ACTIVATION_DATE,
			TssEntityTooltipIdentifierEnum.DEACTIVATION_DATE,
		],
		dateTimeFormat: { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }
	},
	deactivationDatePastDaysBeforeDelete: 30,
	details: {
		allowedDetailsTabs,
		allowedDetailsTabsExternal,
		dateFormat: { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false },
		internalTabContent: detailsFullInternalTabContent,
		internalTabContentTssExternal: detailsFullInternalTabContent.filter( ( tabContent: ITabContent ) => {
			const excludeList: string[] = [
				'fccId'
			];

			return !excludeList.includes( tabContent.key );
		} )
	},
	form: {
		baseData: {
			bsiId: {
				elsterField: true,
				elsterRequiredField: true,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: 'tss.form.bsiId',
				order: 8,
				showInfo: true,
				specialTreatments: null
			},
			deviceType: {
				elsterField: true,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: 'tss.form.deviceType',
				order: 5,
				showInfo: true,
				specialTreatments: null
			},
			ersSecret: {
				hide: false,
				i18nPath: 'tss.form.ersSecret',
				order: 3,
				showInfo: true,
				specialTreatments: null
			},
			name: {
				hide: false,
				i18nPath: 'tss.form.name',
				order: 1,
				showInfo: true,
				specialTreatments: null
			},
			status: {
				hide: false,
				specialTreatments: null,
				i18nPath: 'shared.forms.status',
				order: 4,
				showInfo: true
			},
			tssSerialNumber: {
				elsterField: true,
				elsterRequiredField: true,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: 'tss.form.tssSerialNumber',
				order: 2,
				showInfo: true,
				specialTreatments: null
			}
		},
		preserveData: [
			{ key: 'id', fallbackValue: null },
			{ key: 'etag', fallbackValue: null }
		],
		validatorsTss,
		validatorsTssExternal
	},
	i18n: {
		basePath: APP_CONFIG.tss.i18nPath,
		page: 'page',
		entity: 'entity',
		form: 'form',
		details: 'details',
		tssLifecycleModal: 'modals.tssLifecycle'
	},
	listOperations: listOperationsConfigTss( ListOperationsIdentifierEnum.TSS_LIST_PAGE ),
	listOperationsExternal: listOperationsConfigTssExternal( ListOperationsIdentifierEnum.TSS_EXTERNAL_LIST_PAGE ),
	overview: {
		i18nPath: 'tss'
	},
	presetsRelationByEntityVariant: {
		[ EntityVariantEnum.EXTERNAL ]: {
			ers: ErsPresetEnum.ERS_EXTERNAL
		},
		[ EntityVariantEnum.INTERNAL ]: {
			ers: ErsPresetEnum.ERS
		}
	},
	presetsRelationByPresetId: {
		[ TssPresetEnum.TSS ]: {
			ers: ErsPresetEnum.ERS
		},
		[ TssPresetEnum.TSS_EXTERNAL ]: {
			ers: ErsPresetEnum.ERS_EXTERNAL
		}
	},
	status: {
		actions: [
			{
				actionItem: {
					action: ActionEnum.ACTIVATE,
					actionItemIcon: ActionEnum.REACTIVATE,
					labelI18n: 'shared.buttons.reactivate',
					tooltipI18n: 'shared.buttons.reactivate',
					scopes: TssScopesEnum.StatusEnable
				},
				dialog: {
					i18nPathButtonConfirm: 'reactivate',
					i18nPathDescription: 'tss.entity.dialogForReactivate.description'
				},
				enforceDisableOnStatuses: [],
				enforceHideOnStatuses: [],
				order: 1,
				outOfFlowDisplayMode: OutOfFlowDisplayModeEnum.HIDDEN,
				requestActionStatus: actionStatus.REACTIVATE,
				targetStatus: TssStatusEnum.ACTIVE
			},
			{
				actionItem: {
					action: ActionEnum.DELETE,
					labelI18n: 'shared.buttons.delete',
					tooltipI18n: 'shared.buttons.delete',
					scopes: TssScopesEnum.Delete
				},
				dialog: {
					i18nPathButtonConfirm: 'delete',
					i18nPathDescription: 'tss.entity.dialogForDelete.description'
				},
				enforceDisableOnStatuses: [],
				enforceHideOnStatuses: [ TssStatusEnum.DISABLED, TssStatusEnum.INACTIVE ],
				order: 3,
				outOfFlowDisplayMode: OutOfFlowDisplayModeEnum.HIDDEN,
				requestActionStatus: actionStatus.DELETE,
				targetStatus: TssStatusEnum.DELETED
			},
			{
				actionItem: {
					action: ActionEnum.DISABLE,
					labelI18n: 'shared.buttons.disable',
					tooltipI18n: 'shared.buttons.disable',
					scopes: TssScopesEnum.StatusDisable
				},
				dialog: {
					i18nPathButtonConfirm: 'disable',
					i18nPathDescription: 'tss.entity.dialogForDisabled.description'
				},
				enforceDisableOnStatuses: [],
				enforceHideOnStatuses: [],
				order: 2,
				outOfFlowDisplayMode: OutOfFlowDisplayModeEnum.HIDDEN,
				requestActionStatus: actionStatus.DISABLE,
				targetStatus: TssStatusEnum.DISABLED
			}
		],
		flow: {
			[ TssStatusEnum.ACTIVE ]: [ TssStatusEnum.DISABLED ], // not supported yet -> TssStatusEnum.SUSPENDED
			[ TssStatusEnum.DELETED ]: [],
			[ TssStatusEnum.DISABLED ]: [ TssStatusEnum.DELETED ],
			[ TssStatusEnum.INACTIVE ]: [ TssStatusEnum.DELETED ],
			[ TssStatusEnum.SUSPENDED ]: [ TssStatusEnum.ACTIVE, TssStatusEnum.DISABLED ]
		}
	},
	statusTssExternal: {
		actions: [
			{
				actionItem: {
					action: ActionEnum.ACTIVATE,
					actionItemIcon: ActionEnum.REACTIVATE,
					labelI18n: 'shared.buttons.activate',
					tooltipI18n: 'shared.buttons.activate',
					scopes: TssScopesEnum.StatusEnable
				},
				dialog: {
					i18nPathButtonConfirm: 'activate',
					i18nPathDescription: 'tss.entity.dialogForActivate.descriptionExternal'
				},
				enforceDisableOnStatuses: [],
				enforceHideOnStatuses: [],
				order: 1,
				outOfFlowDisplayMode: OutOfFlowDisplayModeEnum.HIDDEN,
				requestActionStatus: actionStatus.ACTIVATE,
				targetStatus: TssExternalStatusEnum.ACTIVE
			},
			{
				actionItem: {
					action: ActionEnum.DELETE,
					labelI18n: 'shared.buttons.delete',
					tooltipI18n: 'shared.buttons.delete',
					scopes: TssScopesEnum.Delete
				},
				dialog: {
					i18nPathButtonConfirm: 'delete',
					i18nPathDescription: 'tss.entity.dialogForDelete.description'
				},
				enforceDisableOnStatuses: [],
				enforceHideOnStatuses: [ TssExternalStatusEnum.DISABLED, TssExternalStatusEnum.INACTIVE ],
				order: 3,
				outOfFlowDisplayMode: OutOfFlowDisplayModeEnum.HIDDEN,
				requestActionStatus: actionStatus.DELETE,
				targetStatus: TssExternalStatusEnum.DELETED
			},
			{
				actionItem: {
					action: ActionEnum.DISABLE,
					labelI18n: 'shared.buttons.disable',
					tooltipI18n: 'shared.buttons.disable',
					scopes: TssScopesEnum.StatusDisable
				},
				dialog: {
					i18nPathButtonConfirm: 'disable',
					i18nPathDescription: 'tss.entity.dialogForDisabled.descriptionExternal'
				},
				enforceDisableOnStatuses: [],
				enforceHideOnStatuses: [],
				order: 2,
				outOfFlowDisplayMode: OutOfFlowDisplayModeEnum.HIDDEN,
				requestActionStatus: actionStatus.DISABLE,
				targetStatus: TssExternalStatusEnum.DISABLED
			}
		],
		flow: {
			[ TssExternalStatusEnum.ACTIVE ]: [ TssExternalStatusEnum.DISABLED ],
			[ TssExternalStatusEnum.DELETED ]: [],
			[ TssExternalStatusEnum.DISABLED ]: [ TssExternalStatusEnum.ACTIVE, TssExternalStatusEnum.DELETED ],
			[ TssExternalStatusEnum.INACTIVE ]: [ TssExternalStatusEnum.ACTIVE, TssExternalStatusEnum.DELETED ]
		}
	},
	statusAllowedToListTssUnderBusinessPremises: ( entityVariant: EntityVariantEnum ) => {
		const tssEntityVariantStatus = {
			[ EntityVariantEnum.EXTERNAL ]: [
				TssExternalStatusEnum.ACTIVE,
				TssExternalStatusEnum.INACTIVE
			],
			[ EntityVariantEnum.INTERNAL ]: [
				TssStatusEnum.ACTIVE,
				TssStatusEnum.INACTIVE
			]
		};

		return tssEntityVariantStatus[ entityVariant ];
	},
	supportedEntityVariants
} );
